import { _ as _objectSpread2, a as Breakpoint } from './index-03130070.esm.js';
import { isBrowser, themeAttribute } from '@lendi-ui/commons/utils';
import { createGlobalStyle, ThemeContext } from 'styled-components';
import { useContext } from 'react';

var colorPrefixes = ['primary', 'secondary', 'tertiary', 'shade', 'info', 'warn', 'error', 'success']; // All available prefix

var capitalizeFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Custom setThemeName function
 *
 * At runtime, it sets the correct theme to the `document`
 *
 * It replys on `document` `data-attributes` as primary mechanism,
 * ans `sessionStoryage` as a backup mechanism.
 *
 * It works in conjunction with Custom getThemeName from `@lendi-ui/commons`
 *
 * @deprecated Deprecated since 0.2.3. Use SC's Theme instead.
 */
var setThemeName = themeName => {
  // Check browser for runtime
  if (isBrowser) {
    var _window;
    // Get `theme` from DOM `data-attributes` if it's already available.
    var dataTheme = (_window = window) === null || _window === void 0 ? void 0 : _window.document.documentElement.getAttribute(themeAttribute);

    // Check `domTheme` against provided runtime `themeName`
    if (dataTheme !== themeName) {
      var _window2;
      // If different, set runtime `themeName` to data-attributes
      (_window2 = window) === null || _window2 === void 0 ? void 0 : _window2.document.documentElement.setAttribute(themeAttribute, themeName);

      // If different, set runtime `themeName` to sessionStorage
      try {
        var themeValue = JSON.stringify({
          label: capitalizeFirstLetter(themeName),
          value: themeName
        });
        window.sessionStorage.setItem('theme', themeValue);
      } catch (error) {
        // TODO: Report Error
      }
    }
  }
};
var mapToColorsLegacyFormat = colorToken => {
  var colors = {};
  var categories = Object.keys(colorToken);
  var shouldInclude = category => colorPrefixes.includes(category);
  categories.filter(shouldInclude).map(category => {
    var transparencies = Object.keys(colorToken[category]);
    transparencies.map(transparency => {
      colors = _objectSpread2(_objectSpread2({}, colors), {}, {
        ["".concat(category, ".").concat(transparency)]: colorToken[category][transparency]
      });
    });
  });
  return colors;
};

var tokens$3 = {
	asset: {
		font: {
			heading: {
				weight: 700,
				family: "'Cabin', sans-serif",
				metrics: {
					familyName: "Cabin",
					capHeight: 1400,
					ascent: 1930,
					descent: -500,
					lineGap: 0,
					unitsPerEm: 2000
				}
			},
			body: {
				weight: 400,
				family: "'Open Sans', sans-serif",
				metrics: {
					familyName: "Open Sans",
					capHeight: 1462,
					ascent: 2189,
					descent: -600,
					lineGap: 0,
					unitsPerEm: 2048
				}
			}
		}
	},
	color: {
		info: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431",
			"950": "#112431"
		},
		error: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314",
			"950": "#331314"
		},
		success: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210",
			"950": "#0d2210"
		},
		warn: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12",
			"950": "#321e12"
		},
		ultramarine: {
			"25": "#fbfbff",
			"50": "#f6f7ff",
			"100": "#eaecff",
			"200": "#d3d8ff",
			"300": "#a9b3ff",
			"400": "#8291ff",
			"500": "#5e72ff",
			"600": "#3d53f5",
			"700": "#3645b8",
			"800": "#29337b",
			"900": "#171b3d"
		},
		transparent: {
			"0": "rgba(0, 0, 0, 0)"
		},
		white: {
			"0": "#ffffff"
		},
		black: {
			"1000": "#000000"
		},
		green: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210"
		},
		red: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314"
		},
		orange: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12"
		},
		blue: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431"
		},
		primary: {
			"25": "#f2fbfa",
			"50": "#e5f8f6",
			"100": "#ccf2ed",
			"200": "#99e5db",
			"300": "#66d9c9",
			"400": "#32ccb7",
			"500": "#00c0a5",
			"600": "#329984",
			"700": "#007363",
			"800": "#004c42",
			"900": "#002621",
			"950": "#001310"
		},
		secondary: {
			"25": "#f2f6f7",
			"50": "#e6eef0",
			"100": "#cedee1",
			"200": "#9ebdc4",
			"300": "#6d9ca7",
			"400": "#3d7a8a",
			"500": "#0d5a6d",
			"600": "#0a4857",
			"700": "#073641",
			"800": "#05242b",
			"900": "#021215",
			"950": "#01080a"
		},
		tertiary: {
			"25": "#fff7f7",
			"50": "#fff0ef",
			"100": "#ffe2df",
			"200": "#ffc5c0",
			"300": "#ffa9a1",
			"400": "#ff8c82",
			"500": "#ff7063",
			"600": "#cc594f",
			"700": "#99433b",
			"800": "#662c27",
			"900": "#331613",
			"950": "#190b09"
		},
		shade: {
			"0": "#ffffff",
			"25": "#f8f8f8",
			"50": "#f1f1f1",
			"100": "#e3e3e3",
			"200": "#c8c8c8",
			"300": "#adadad",
			"400": "#929292",
			"500": "#777777",
			"600": "#5f5f5f",
			"700": "#474747",
			"800": "#2f2f2f",
			"900": "#171717",
			"950": "#0b0b0b",
			"1000": "#000000"
		},
		text: {
			main: "#474747",
			subtle: "#5f5f5f",
			inverse: "#ffffff",
			contrast: "#0d5a6d"
		},
		interaction: {
			main: "#00c0a5",
			subtle: "#ffffff",
			contrast: "#00c0a5",
			emphasis: "#ff7063",
			disabled: "#474747",
			success: "#2cac41",
			error: "#ff282f"
		},
		fill: {
			main: "#ffffff",
			selected: "#e5f8f6",
			disabled: "#f8f8f8",
			accent: "#00c0a5",
			contrast: "#0d5a6d"
		},
		background: {
			page: "#f8f8f8",
			container: "#ffffff",
			surface: "#e3e3e3",
			emphasis: "#f1f1f1"
		},
		border: {
			container: "#e3e3e3",
			divider: "#adadad",
			dividerEmphasis: "#2f2f2f",
			interaction: {
				"default": "#c8c8c8",
				hover: "#0d5a6d",
				selected: "#00c0a5",
				focus: "#3f86f7"
			}
		},
		buttonFill: {
			primary: {
				"default": "#00c0a5",
				hover: "#32ccb7",
				focus: "#329984",
				disabled: "#00c0a5",
				pressed: "#329984"
			},
			primaryInverse: {
				"default": "#ff7063",
				hover: "#ffffff",
				focus: "#ff7063",
				disabled: "#f8f8f8",
				pressed: "#ff7063"
			},
			secondary: {
				"default": "#ffffff",
				hover: "#ffffff",
				focus: "#ffffff",
				disabled: "#ffffff",
				pressed: "#e5f8f6"
			},
			secondaryInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#ffffff",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#e5f8f6"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "rgba(0, 0, 0, 0)",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "rgba(0, 0, 0, 0)"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "rgba(0, 0, 0, 0)",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "rgba(0, 0, 0, 0)"
			}
		},
		buttonForeground: {
			primary: {
				"default": "#ffffff",
				hover: "#ffffff",
				focus: "#ffffff",
				disabled: "#ffffff",
				pressed: "#ffffff"
			},
			primaryInverse: {
				"default": "#ffffff",
				hover: "#ff7063",
				focused: "#ffffff",
				disabled: "#474747",
				pressed: "#ffffff"
			},
			secondary: {
				"default": "#00c0a5",
				hover: "#00c0a5",
				focus: "#00c0a5",
				disabled: "#00c0a5",
				pressed: "#329984"
			},
			secondaryInverse: {
				"default": "#ffffff",
				hover: "#00c0a5",
				focused: "#ffffff",
				disabled: "#ffffff",
				pressed: "#329984"
			},
			text: {
				"default": "#00c0a5",
				hover: "#00c0a5",
				focus: "#00c0a5",
				disabled: "#00c0a5",
				pressed: "#329984"
			},
			textInverse: {
				"default": "#ffffff",
				hover: "#00c0a5",
				focus: "#ffffff",
				disabled: "#ffffff",
				pressed: "#ffffff"
			}
		},
		buttonBorder: {
			primary: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "rgba(0, 0, 0, 0)"
			},
			primaryInverse: {
				pressed: "#ff7063",
				"default": "#ff7063",
				hover: "#ffffff",
				focus: "#ff7063",
				disabled: "#474747"
			},
			secondary: {
				"default": "#00c0a5",
				hover: "#32ccb7",
				focus: "#00c0a5",
				disabled: "#00c0a5",
				pressed: "#329984"
			},
			secondaryInverse: {
				"default": "#ffffff",
				hover: "#00c0a5",
				focus: "#ffffff",
				disabled: "#ffffff",
				pressed: "#329984"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "rgba(0, 0, 0, 0)",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "rgba(0, 0, 0, 0)"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "rgba(0, 0, 0, 0)",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "rgba(0, 0, 0, 0)"
			}
		}
	},
	size: {
		breakpoint: {
			mobile: "0rem",
			tablet: "36rem",
			desktop: "62rem",
			sm: "36rem",
			md: "48rem",
			lg: "62rem",
			xl: "75rem"
		}
	}
};

var themeName$3 = 'lendi';

/**
 * @deprecated Use tokens instead
 */
var typography$2 = {
  /**
   * @deprecated Use `asset.font.heading.family` instead
   */
  heading: {
    fontFamily: tokens$3.asset.font.heading.family
  },
  /**
   * @deprecated Use `asset.font.body.family` instead
   */
  body: {
    fontFamily: tokens$3.asset.font.body.family
  }
};

/**
 * @deprecated Use `theme.name` instead
 * @example
 * import { useTheme } from '@lendi/ui/Theme';
 *
 * const theme = useTheme();
 * const themeName = theme.name;
 */
var logo$1 = {
  logoName: 'LendiLogo'
};
var borderRadius$1 = '6px';

/**
 * @deprecated Use `theme.color.xxx.yyy` instead. Will be removed in future major release.
 * @example
 * import { useTheme } from '@lendi/ui/Theme';
 *
 * const theme = useTheme();
 * theme.color.interaction.main;
 */
// @ts-expect-error
var colors$1 = mapToColorsLegacyFormat(tokens$3.color);
var theme = _objectSpread2(_objectSpread2({
  typography: typography$2,
  colors: colors$1,
  logo: logo$1,
  borderRadius: borderRadius$1
}, tokens$3), {}, {
  name: themeName$3
});

var tokens$2 = {
	asset: {
		font: {
			heading: {
				weight: 700,
				family: "'F37 Bolton', Helvetica, Arial, sans-serif",
				metrics: {
					fontFamily: "F37 Bolton",
					capHeight: 717,
					ascent: 714,
					descent: -286,
					lineGap: 250,
					unitsPerEm: 1000
				}
			},
			body: {
				weight: 400,
				family: "'F37 Bolton', Helvetica, Arial, sans-serif",
				metrics: {
					fontFamily: "F37 Bolton",
					capHeight: 717,
					ascent: 714,
					descent: -286,
					lineGap: 250,
					unitsPerEm: 1000
				}
			}
		}
	},
	color: {
		info: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431",
			"950": "#112431"
		},
		error: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314",
			"950": "#331314"
		},
		success: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210",
			"950": "#0d2210"
		},
		warn: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12",
			"950": "#321e12"
		},
		ultramarine: {
			"25": "#fbfbff",
			"50": "#f6f7ff",
			"100": "#eaecff",
			"200": "#d3d8ff",
			"300": "#a9b3ff",
			"400": "#8291ff",
			"500": "#5e72ff",
			"600": "#3d53f5",
			"700": "#3645b8",
			"800": "#29337b",
			"900": "#171b3d"
		},
		transparent: {
			"0": "rgba(0, 0, 0, 0)"
		},
		white: {
			"0": "#ffffff"
		},
		black: {
			"1000": "#000000"
		},
		green: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210"
		},
		red: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314"
		},
		orange: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12"
		},
		blue: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431"
		},
		primary: {
			"25": "#e4ffe2",
			"50": "#caffc5",
			"100": "#abfca4",
			"200": "#85f27c",
			"300": "#56d94a",
			"400": "#2ec121",
			"500": "#0ea800",
			"600": "#16860b",
			"700": "#186511",
			"800": "#154311",
			"900": "#0d220b",
			"950": "#0d220b"
		},
		secondary: {
			"25": "#f4f8fe",
			"50": "#eaf1fd",
			"100": "#dae5f9",
			"200": "#c1d2ee",
			"300": "#98aed4",
			"400": "#748eba",
			"500": "#5470a1",
			"600": "#3a5687",
			"700": "#25406e",
			"800": "#142c54",
			"900": "#0e182a",
			"950": "#0e182a"
		},
		tertiary: {
			"25": "#f8f7ff",
			"50": "#f1efff",
			"100": "#e4e1ff",
			"200": "#cdc8ff",
			"300": "#a89ffb",
			"400": "#857ae9",
			"500": "#6458d7",
			"600": "#483bc5",
			"700": "#3b3194",
			"800": "#292363",
			"900": "#161331",
			"950": "#161331"
		},
		lime: {
			"25": "#e4ffe2",
			"50": "#caffc5",
			"100": "#abfca4",
			"200": "#85f27c",
			"300": "#56d94a",
			"400": "#2ec121",
			"500": "#0ea800",
			"600": "#16860b",
			"700": "#186511",
			"800": "#154311",
			"900": "#0d220b"
		},
		navy: {
			"25": "#f4f8fe",
			"50": "#eaf1fd",
			"100": "#dae5f9",
			"200": "#c1d2ee",
			"300": "#98aed4",
			"400": "#748eba",
			"500": "#5470a1",
			"600": "#3a5687",
			"700": "#25406e",
			"800": "#142c54",
			"900": "#0e182a"
		},
		ocean: {
			"25": "#f8f7ff",
			"50": "#f1efff",
			"100": "#e4e1ff",
			"200": "#cdc8ff",
			"300": "#a89ffb",
			"400": "#857ae9",
			"500": "#6458d7",
			"600": "#483bc5",
			"700": "#3b3194",
			"800": "#292363",
			"900": "#161331"
		},
		vermillion: {
			"25": "#fff5f3",
			"50": "#ffe9e5",
			"100": "#ffd8d1",
			"200": "#ffbeb2",
			"300": "#ff9684",
			"400": "#f8715a",
			"500": "#e84f34",
			"600": "#d93013",
			"700": "#a3301c",
			"800": "#6d281c",
			"900": "#361712"
		},
		grey: {
			"25": "#fbfbfc",
			"50": "#f8f8f9",
			"100": "#eff0f3",
			"200": "#dbdee5",
			"300": "#b5bcca",
			"400": "#929cae",
			"500": "#727d92",
			"600": "#556177",
			"700": "#3c475b",
			"800": "#232c3d",
			"900": "#0f141e"
		},
		shade: {
			"0": "#ffffff",
			"25": "#fbfbfc",
			"50": "#f8f8f9",
			"100": "#eff0f3",
			"200": "#dbdee5",
			"300": "#b5bcca",
			"400": "#929cae",
			"500": "#727d92",
			"600": "#556177",
			"700": "#3c475b",
			"800": "#232c3d",
			"900": "#0f141e",
			"950": "#0b0b0b",
			"1000": "#000000"
		},
		text: {
			main: "#0f141e",
			subtle: "#3c475b",
			contrast: "#142c54",
			inverse: "#ffffff",
			"default": "#0f141e"
		},
		interaction: {
			main: "#0ea800",
			subtle: "#ffffff",
			contrast: "#0ea800",
			emphasis: "#d93013",
			disabled: "#929cae",
			success: "#288a38",
			error: "#cc2b30"
		},
		fill: {
			main: "#ffffff",
			contrast: "#142c54",
			accent: "#0ea800",
			selected: "#eaf1fd",
			disabled: "#f8f8f9"
		},
		background: {
			page: "#fbfbfc",
			container: "#ffffff",
			surface: "#eff0f3",
			emphasis: "#f8f8f9"
		},
		border: {
			container: "#dbdee5",
			divider: "#eff0f3",
			dividerEmphasis: "#0f141e",
			interaction: {
				"default": "#727d92",
				hover: "#3c475b",
				focus: "#5e72ff",
				selected: "#142c54"
			}
		},
		buttonFill: {
			primary: {
				"default": "#0ea800",
				hover: "#2ec121",
				focus: "#0ea800",
				disabled: "#929cae",
				pressed: "#16860b"
			},
			primaryInverse: {
				"default": "#ffffff",
				hover: "#e4ffe2",
				focus: "#ffffff",
				disabled: "#f8f8f9",
				pressed: "#caffc5"
			},
			secondary: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#e4ffe2",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#caffc5"
			},
			secondaryInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#25406e",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#0e182a"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#e4ffe2",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#caffc5"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#25406e",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#0e182a"
			}
		},
		buttonForeground: {
			primary: {
				"default": "#ffffff",
				hover: "#ffffff",
				focus: "#ffffff",
				disabled: "#ffffff",
				pressed: "#ffffff"
			},
			primaryInverse: {
				"default": "#0ea800",
				hover: "#0ea800",
				focused: "#0ea800",
				disabled: "#929cae",
				pressed: "#0ea800"
			},
			secondary: {
				"default": "#0ea800",
				hover: "#0ea800",
				focused: "#0ea800",
				disabled: "#929cae",
				pressed: "#0ea800"
			},
			secondaryInverse: {
				"default": "#ffffff",
				hover: "#ffffff",
				focused: "#ffffff",
				disabled: "#929cae",
				pressed: "#ffffff"
			},
			text: {
				"default": "#0ea800",
				hover: "#0ea800",
				focused: "#0ea800",
				disabled: "#929cae",
				pressed: "#0ea800"
			},
			textInverse: {
				"default": "#ffffff",
				hover: "#ffffff",
				focused: "#ffffff",
				disabled: "#929cae",
				pressed: "#ffffff"
			}
		},
		buttonBorder: {
			primary: {
				"default": "#0ea800",
				hover: "#2ec121",
				focus: "#0ea800",
				disabled: "#929cae",
				pressed: "#16860b"
			},
			primaryInverse: {
				"default": "#ffffff",
				hover: "#e4ffe2",
				focus: "#ffffff",
				disabled: "#f8f8f9",
				pressed: "#caffc5"
			},
			secondary: {
				"default": "#0ea800",
				hover: "#0ea800",
				focus: "#0ea800",
				disabled: "#929cae",
				pressed: "#0ea800"
			},
			secondaryInverse: {
				"default": "#ffffff",
				focus: "#ffffff",
				hover: "#ffffff",
				pressed: "#ffffff",
				disabled: "#929cae"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#e4ffe2",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#caffc5"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#25406e",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#0e182a"
			}
		}
	},
	size: {
		breakpoint: {
			mobile: "0rem",
			tablet: "36rem",
			desktop: "62rem",
			sm: "36rem",
			md: "48rem",
			lg: "62rem",
			xl: "75rem"
		}
	}
};

var themeName$2 = 'domain';

/**
 * @deprecated Use tokens instead
 */
var typography$1 = {
  /**
   * @deprecated Use `asset.font.heading.family` instead
   */
  heading: {
    fontFamily: tokens$2.asset.font.heading.family
  },
  /**
   * @deprecated Use `asset.font.body.family` instead
   */
  body: {
    fontFamily: tokens$2.asset.font.body.family
  }
};

/**
 * @deprecated Use `theme.name` instead
 * @example
 * import { useTheme } from '@lendi/ui/Theme';
 *
 * const theme = useTheme();
 * const themeName = theme.name;
 */
var logo = {
  logoName: 'DomainLogo'
};
var borderRadius = '3px';

/**
 * @deprecated Use `theme.color.xxx.yyy` instead. Will be removed in future major release.
 * @example
 * const theme = useTheme();
 * theme.color.interaction.main;
 */
// @ts-expect-error
var colors = mapToColorsLegacyFormat(tokens$2.color);

/**
 * @since 9.2.3
 */

var domainTheme = _objectSpread2(_objectSpread2({
  typography: typography$1,
  colors,
  logo,
  borderRadius
}, tokens$2), {}, {
  name: themeName$2
});

var tokens$1 = {
	asset: {
		font: {
			heading: {
				weight: 700,
				family: "'Cabin', sans-serif",
				metrics: {
					familyName: "Cabin",
					capHeight: 1400,
					ascent: 1930,
					descent: -500,
					lineGap: 0,
					unitsPerEm: 2000
				}
			},
			body: {
				weight: 400,
				family: "'Open Sans', sans-serif",
				metrics: {
					familyName: "Open Sans",
					capHeight: 1462,
					ascent: 2189,
					descent: -600,
					lineGap: 0,
					unitsPerEm: 2048
				}
			}
		}
	},
	color: {
		info: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431",
			"950": "#112431"
		},
		error: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314",
			"950": "#331314"
		},
		success: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210",
			"950": "#0d2210"
		},
		warn: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12",
			"950": "#321e12"
		},
		ultramarine: {
			"25": "#fbfbff",
			"50": "#f6f7ff",
			"100": "#eaecff",
			"200": "#d3d8ff",
			"300": "#a9b3ff",
			"400": "#8291ff",
			"500": "#5e72ff",
			"600": "#3d53f5",
			"700": "#3645b8",
			"800": "#29337b",
			"900": "#171b3d"
		},
		transparent: {
			"0": "rgba(0, 0, 0, 0)"
		},
		white: {
			"0": "#ffffff"
		},
		black: {
			"1000": "#000000"
		},
		green: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210"
		},
		red: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314"
		},
		orange: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12"
		},
		blue: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431"
		},
		tiber: {
			"25": "#f0fafc",
			"50": "#e0f5fa",
			"100": "#cdedf4",
			"200": "#b0dce6",
			"300": "#85becb",
			"400": "#61a1b0",
			"500": "#428594",
			"600": "#296a79",
			"700": "#15505d",
			"800": "#083742",
			"900": "#091d21"
		},
		turquoise: {
			"25": "#dbfffa",
			"50": "#b7fff5",
			"100": "#8efced",
			"200": "#60f1dd",
			"300": "#2bd9c2",
			"400": "#00c0a5",
			"500": "#0ba08b",
			"600": "#128071",
			"700": "#146056",
			"800": "#12403a",
			"900": "#0b201d"
		},
		lagoon: {
			"25": "#e7faff",
			"50": "#cff5ff",
			"100": "#b3ebfb",
			"200": "#8fdbf0",
			"300": "#60bdd7",
			"400": "#39a1be",
			"500": "#1987a5",
			"600": "#006e8c",
			"700": "#0b5569",
			"800": "#0f3a46",
			"900": "#0b1e23"
		},
		coral: {
			"25": "#fff6f5",
			"50": "#ffebe9",
			"100": "#ffdad7",
			"200": "#ffc0ba",
			"300": "#ff978d",
			"400": "#ff7063",
			"500": "#d45e54",
			"600": "#aa4c44",
			"700": "#803a34",
			"800": "#552723",
			"900": "#2b1412"
		},
		grey: {
			"25": "#fafafa",
			"50": "#f5f5f5",
			"100": "#ececec",
			"200": "#dcdcdc",
			"300": "#bfbfbf",
			"400": "#a1a1a1",
			"500": "#838383",
			"600": "#656565",
			"700": "#474747",
			"800": "#2f2f2f",
			"900": "#181818"
		},
		shade: {
			"0": "#ffffff",
			"25": "#fafafa",
			"50": "#f5f5f5",
			"100": "#ececec",
			"200": "#dcdcdc",
			"300": "#bfbfbf",
			"400": "#a1a1a1",
			"500": "#838383",
			"600": "#656565",
			"700": "#474747",
			"800": "#2f2f2f",
			"900": "#181818",
			"950": "#0b0b0b",
			"1000": "#000000"
		},
		text: {
			main: "#181818",
			subtle: "#474747",
			contrast: "#083742",
			inverse: "#ffffff"
		},
		interaction: {
			main: "#006e8c",
			subtle: "#ffffff",
			contrast: "#0ba08b",
			emphasis: "#ff7063",
			disabled: "#a1a1a1",
			success: "#288a38",
			error: "#cc2b30"
		},
		fill: {
			main: "#ffffff",
			contrast: "#006e8c",
			accent: "#00c0a5",
			selected: "#cff5ff",
			disabled: "#f5f5f5"
		},
		background: {
			page: "#fafafa",
			container: "#ffffff",
			surface: "#ececec",
			emphasis: "#f5f5f5"
		},
		border: {
			container: "#dcdcdc",
			divider: "#ececec",
			dividerEmphasis: "#181818",
			interaction: {
				"default": "#838383",
				hover: "#474747",
				focus: "#5e72ff",
				selected: "#006e8c"
			}
		},
		buttonFill: {
			primary: {
				"default": "#006e8c",
				hover: "#0b5569",
				focus: "#006e8c",
				disabled: "#a1a1a1",
				pressed: "#0f3a46"
			},
			primaryInverse: {
				"default": "#ffffff",
				hover: "#e7faff",
				focus: "#ffffff",
				disabled: "#f5f5f5",
				pressed: "#cff5ff"
			},
			secondary: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#e7faff",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#cff5ff"
			},
			secondaryInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#15505d",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#091d21"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#e7faff",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#cff5ff"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#15505d",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#091d21"
			}
		},
		buttonForeground: {
			primary: {
				"default": "#ffffff",
				hover: "#ffffff",
				disabled: "#ffffff",
				pressed: "#ffffff"
			},
			primaryInverse: {
				"default": "#006e8c",
				hover: "#006e8c",
				focus: "#006e8c",
				disabled: "#a1a1a1",
				pressed: "#006e8c"
			},
			secondary: {
				"default": "#006e8c",
				hover: "#006e8c",
				focus: "#006e8c",
				disabled: "#a1a1a1",
				pressed: "#006e8c"
			},
			secondaryInverse: {
				"default": "#ffffff",
				hover: "#ffffff",
				focus: "#ffffff",
				disabled: "#a1a1a1",
				pressed: "#ffffff"
			},
			text: {
				"default": "#006e8c",
				hover: "#006e8c",
				focus: "#006e8c",
				disabled: "#a1a1a1",
				pressed: "#006e8c"
			},
			textInverse: {
				"default": "#ffffff",
				hover: "#ffffff",
				focus: "#ffffff",
				disabled: "#a1a1a1",
				pressed: "#ffffff"
			}
		},
		buttonBorder: {
			primary: {
				"default": "#006e8c",
				hover: "#0b5569",
				focus: "#006e8c",
				disabled: "#a1a1a1",
				pressed: "#0f3a46"
			},
			primaryInverse: {
				"default": "#ffffff",
				hover: "#e7faff",
				focus: "#ffffff",
				disabled: "#f5f5f5",
				pressed: "#cff5ff"
			},
			secondary: {
				"default": "#006e8c",
				hover: "#006e8c",
				focus: "#006e8c",
				disabled: "#a1a1a1",
				pressed: "#006e8c"
			},
			secondaryInverse: {
				"default": "#ffffff",
				focus: "#ffffff",
				hover: "#ffffff",
				pressed: "#ffffff",
				disabled: "#a1a1a1"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#e7faff",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#cff5ff"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#15505d",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#091d21"
			}
		}
	},
	size: {
		breakpoint: {
			mobile: "0rem",
			tablet: "36rem",
			desktop: "62rem",
			sm: "36rem",
			md: "48rem",
			lg: "62rem",
			xl: "75rem"
		}
	}
};

var themeName$1 = 'lendi-next';
var lendiNextTheme = _objectSpread2(_objectSpread2(_objectSpread2({}, theme), tokens$1), {}, {
  name: themeName$1
});

var tokens = {
	asset: {
		font: {
			heading: {
				weight: 700,
				family: "'TT Commons Pro', sans-serif",
				metrics: {
					familyName: "TT Commons Pro",
					capHeight: 700,
					ascent: 1000,
					descent: -200,
					lineGap: 0,
					unitsPerEm: 1000
				}
			},
			body: {
				weight: 400,
				family: "'Barlow', sans-serif",
				metrics: {
					familyName: "Barlow",
					capHeight: 700,
					ascent: 1000,
					descent: -200,
					lineGap: 0,
					unitsPerEm: 1000
				}
			}
		}
	},
	color: {
		info: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431",
			"950": "#112431"
		},
		error: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314",
			"950": "#331314"
		},
		success: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210",
			"950": "#0d2210"
		},
		warn: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12",
			"950": "#321e12"
		},
		ultramarine: {
			"25": "#fbfbff",
			"50": "#f6f7ff",
			"100": "#eaecff",
			"200": "#d3d8ff",
			"300": "#a9b3ff",
			"400": "#8291ff",
			"500": "#5e72ff",
			"600": "#3d53f5",
			"700": "#3645b8",
			"800": "#29337b",
			"900": "#171b3d"
		},
		transparent: {
			"0": "rgba(0, 0, 0, 0)"
		},
		white: {
			"0": "#ffffff"
		},
		black: {
			"1000": "#000000"
		},
		green: {
			"25": "#ecffef",
			"50": "#d9ffdf",
			"100": "#c1fecb",
			"200": "#a0f4ae",
			"300": "#73dc84",
			"400": "#4cc460",
			"500": "#2cac41",
			"600": "#288a38",
			"700": "#21672d",
			"800": "#18451f",
			"900": "#0d2210"
		},
		red: {
			"25": "#fff3f3",
			"50": "#ffe5e6",
			"100": "#ffcfd1",
			"200": "#ffafb2",
			"300": "#ff7f84",
			"400": "#ff5158",
			"500": "#ff282f",
			"600": "#cc2b30",
			"700": "#99282c",
			"800": "#662022",
			"900": "#331314"
		},
		orange: {
			"25": "#fff7f2",
			"50": "#ffeee4",
			"100": "#ffe0ce",
			"200": "#ffcbad",
			"300": "#ffac7c",
			"400": "#ff904f",
			"500": "#fc7426",
			"600": "#ca6429",
			"700": "#975027",
			"800": "#65391f",
			"900": "#321e12"
		},
		blue: {
			"25": "#f0f9ff",
			"50": "#def1ff",
			"100": "#c5e7ff",
			"200": "#a2d8ff",
			"300": "#70c3ff",
			"400": "#42b0ff",
			"500": "#1898f5",
			"600": "#207fc4",
			"700": "#216393",
			"800": "#1d4562",
			"900": "#112431"
		},
		purple: {
			"25": "#faf3fe",
			"50": "#f3e6fc",
			"100": "#e9d5f7",
			"200": "#d8bbeb",
			"300": "#b791d1",
			"400": "#996db7",
			"500": "#7d4e9c",
			"600": "#633482",
			"700": "#4b1f68",
			"800": "#331745",
			"900": "#1a0d23"
		},
		yellow: {
			"25": "#fffbe9",
			"50": "#fff6d1",
			"100": "#ffefb1",
			"200": "#ffe788",
			"300": "#ffdc51",
			"400": "#ffd11f",
			"500": "#d4b024",
			"600": "#aa8f26",
			"700": "#806d23",
			"800": "#55491b",
			"900": "#2b2610"
		},
		teal: {
			"25": "#ecfffb",
			"50": "#d8fff7",
			"100": "#bffff1",
			"200": "#9dffea",
			"300": "#6eedd1",
			"400": "#45d9ba",
			"500": "#3db59b",
			"600": "#33917d",
			"700": "#286d5e",
			"800": "#1c483e",
			"900": "#0f241f"
		},
		peach: {
			"25": "#fff8f5",
			"50": "#fff0ea",
			"100": "#ffe4d9",
			"200": "#ffd0bc",
			"300": "#ffb18f",
			"400": "#ff9366",
			"500": "#d47b56",
			"600": "#aa6345",
			"700": "#804b34",
			"800": "#553223",
			"900": "#2b1a12"
		},
		grey: {
			"25": "#fcfcf9",
			"50": "#f8f8f6",
			"100": "#f2f2f0",
			"200": "#e4e4e2",
			"300": "#c7c7c5",
			"400": "#ababa9",
			"500": "#8f8f8e",
			"600": "#737372",
			"700": "#575756",
			"800": "#3a3a39",
			"900": "#1d1d1d"
		},
		shade: {
			"0": "#ffffff",
			"25": "#fcfcf9",
			"50": "#f8f8f6",
			"100": "#f2f2f0",
			"200": "#e4e4e2",
			"300": "#c7c7c5",
			"400": "#ababa9",
			"500": "#8f8f8e",
			"600": "#737372",
			"700": "#575756",
			"800": "#3a3a39",
			"900": "#1d1d1d",
			"950": "#0b0b0b",
			"1000": "#000000"
		},
		text: {
			main: "#1d1d1d",
			subtle: "#575756",
			contrast: "#4b1f68",
			inverse: "#ffffff",
			"default": "#1d1d1d"
		},
		interaction: {
			main: "#4b1f68",
			subtle: "#ffffff",
			contrast: "#4b1f68",
			emphasis: "#ffd11f",
			disabled: "#ababa9",
			success: "#288a38",
			error: "#cc2b30"
		},
		fill: {
			main: "#ffffff",
			contrast: "#4b1f68",
			accent: "#4b1f68",
			selected: "#f3e6fc",
			disabled: "#f8f8f6"
		},
		background: {
			page: "#fcfcf9",
			container: "#ffffff",
			surface: "#f2f2f0",
			emphasis: "#f8f8f6"
		},
		border: {
			container: "#e4e4e2",
			divider: "#f2f2f0",
			dividerEmphasis: "#1d1d1d",
			interaction: {
				"default": "#8f8f8e",
				hover: "#575756",
				focus: "#5e72ff",
				selected: "#4b1f68"
			}
		},
		buttonFill: {
			primary: {
				"default": "#4b1f68",
				hover: "#633482",
				focus: "#4b1f68",
				disabled: "#ababa9",
				pressed: "#331745"
			},
			primaryInverse: {
				"default": "#ffd11f",
				hover: "#ffdc51",
				focus: "#ffd11f",
				disabled: "#f8f8f6",
				pressed: "#d4b024"
			},
			secondary: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#faf3fe",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#f3e6fc"
			},
			secondaryInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#633482",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#331745"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#faf3fe",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#f3e6fc"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#633482",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#331745"
			}
		},
		buttonForeground: {
			primary: {
				"default": "#ffffff",
				hover: "#ffffff",
				focus: "#ffffff",
				disabled: "#ffffff",
				pressed: "#ffffff"
			},
			primaryInverse: {
				"default": "#4b1f68",
				hover: "#4b1f68",
				focus: "#4b1f68",
				disabled: "#ababa9",
				pressed: "#4b1f68"
			},
			secondary: {
				"default": "#4b1f68",
				hover: "#4b1f68",
				focus: "#4b1f68",
				disabled: "#ababa9",
				pressed: "#4b1f68"
			},
			secondaryInverse: {
				"default": "#ffd11f",
				hover: "#ffd11f",
				focused: "#ffd11f",
				disabled: "#ababa9",
				pressed: "#ffd11f"
			},
			text: {
				"default": "#4b1f68",
				hover: "#4b1f68",
				focus: "#4b1f68",
				disabled: "#ababa9",
				pressed: "#4b1f68"
			},
			textInverse: {
				"default": "#ffd11f",
				hover: "#ffd11f",
				focused: "#ffd11f",
				disabled: "#ababa9",
				pressed: "#ffd11f"
			}
		},
		buttonBorder: {
			primary: {
				"default": "#4b1f68",
				hover: "#633482",
				focus: "#4b1f68",
				disabled: "#ababa9",
				pressed: "#331745"
			},
			primaryInverse: {
				"default": "#ffd11f",
				hover: "#ffdc51",
				focus: "#ffd11f",
				disabled: "#f8f8f6",
				pressed: "#d4b024"
			},
			secondary: {
				"default": "#4b1f68",
				hover: "#4b1f68",
				focus: "#4b1f68",
				disabled: "#ababa9",
				pressed: "#4b1f68"
			},
			secondaryInverse: {
				"default": "#ffd11f",
				hover: "#ffd11f",
				focus: "#ffd11f",
				disabled: "#ababa9",
				pressed: "#ffd11f"
			},
			text: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#faf3fe",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#f3e6fc"
			},
			textInverse: {
				"default": "rgba(0, 0, 0, 0)",
				hover: "#633482",
				focus: "rgba(0, 0, 0, 0)",
				disabled: "rgba(0, 0, 0, 0)",
				pressed: "#331745"
			}
		}
	},
	size: {
		breakpoint: {
			mobile: "0rem",
			tablet: "36rem",
			desktop: "62rem",
			sm: "36rem",
			md: "48rem",
			lg: "62rem",
			xl: "75rem"
		}
	}
};

var themeName = 'aussie';

/**
 * @deprecated Use tokens instead
 */
var typography = {
  /**
   * @deprecated Use `asset.font.heading.family` instead
   */
  heading: {
    fontFamily: tokens.asset.font.heading.family
  },
  /**
   * @deprecated Use `asset.font.body.family` instead
   */
  body: {
    fontFamily: tokens.asset.font.body.family
  }
};
var aussieTheme = _objectSpread2(_objectSpread2(_objectSpread2({}, theme), {}, {
  typography
}, tokens), {}, {
  name: themeName
});

// Any cross browser resets should occur here
var GlobalStyle = createGlobalStyle(["html{box-sizing:border-box;font-size:100%;}*,*:before,*:after{box-sizing:inherit;}html,body{margin:0;padding:0;}body{color:", ";font-family:", ";}h1,h2,h3,h4,h5,h6{font-family:", ";font-weight:", ";}input::-ms-clear{display:none;}:root{--lendi-ui-size:14px;}@media (min-width:", "){:root{--lendi-ui-size:16px;}}.sr-only{border:0 !important;clip:rect(1px,1px,1px,1px) !important;clip-path:inset(50%) !important;height:1px !important;overflow:hidden !important;padding:0 !important;position:absolute !important;width:1px !important;white-space:nowrap !important;}.sr-only-focusable:focus,.sr-only-focusable:active{clip:auto !important;clip-path:none !important;height:auto !important;overflow:visible !important;width:auto !important;white-space:normal !important;}"], _ref => {
  var {
    theme
  } = _ref;
  return theme.color.text.main;
}, _ref2 => {
  var {
    theme
  } = _ref2;
  return theme.asset.font.body.family;
}, _ref3 => {
  var {
    theme
  } = _ref3;
  return theme.asset.font.heading.family;
}, _ref4 => {
  var {
    theme
  } = _ref4;
  return theme.asset.font.heading.weight;
}, Breakpoint.sm);

/**
 * useTheme is a custom hook to return theme object.
 *
 * It is natively available in Style Component v5. However since Lendi's using v4, this custom hook is necessary.
 */
var useTheme = () => {
  return useContext(ThemeContext);
};

export { GlobalStyle as G, aussieTheme as a, domainTheme as d, lendiNextTheme as l, setThemeName as s, theme as t, useTheme as u };
